<template>
  <div class="flex justify-between gap-2 px-6">
    <div class="flex flex-wrap items-start sm:flex-nowrap gap-2">
      <DjImage :src="imageUrl" :width="84.5" :height="84.5" :alt="name" />
      <div class="flex flex-col gap-1 py-[4.75px] w-[270px]">
        <NuxtLink
          :to="productPageLink"
          class="text-black text-base font-semibold"
          @click="emit('close')"
        >
          {{ name }}
        </NuxtLink>
        <p class="flex items-center gap-1">
          <span
            class="text-sm leading-[100%] font-semibold uppercase text-[var(--color-black-400)] whitespace-nowrap"
          >
            {{ $t('DjCartLayer.adiscoRef') }} :
          </span>
          <UTooltip :text="adiscoRef" :delay-duration="tooltipDelay">
            <span
              class="text-base leading-[100%] font-semibold uppercase text-[var(--color-black-650)] truncate"
            >
              {{ adiscoRef }}
            </span>
          </UTooltip>
        </p>
        <div class="flex items-center gap-1">
          <span
            class="text-sm leading-[100%] font-semibold uppercase text-[var(--color-black-400)] whitespace-nowrap"
          >
            {{ $t('DjCartLayer.supplier') }} :
          </span>

          <UTooltip :text="supplier" :delay-duration="tooltipDelay">
            <span
              class="text-base leading-[100%] font-semibold uppercase text-[var(--color-black-650)] truncate"
            >
              {{ supplier }}
            </span>
          </UTooltip>
        </div>
        <div class="flex flex-row justify-between">
          <DjQuantity
            :key="increment"
            :model-value="cartLine.quantity"
            disabled
            :min="Math.max(cartLine.offer.itemPerPack, cartLine.offer.minOrderQuantity)"
            :max="Math.min(cartLine.offer.maxOrderQuantity ?? 100000000, cartLine.offer.stock)"
            :step="cartLine.offer.itemPerPack"
            icon="djust-icon:box-gray"
            rounded="12px"
            class="!w-[104px] h-[26px]"
            @update:model-value="updateQuantity"
          />
          <div class="flex flex-col items-end gap-2">
            <span class="text-base font-semibold text-[var(--color-black-650)] leading-[11px]">
              {{ price }}
            </span>
            <span v-if="hasTax" class="text-sm text-[#999999] leading-[10px]">
              {{ $t('DjCartLayer.tax') }} {{ tgap }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <UButton
      :key="cartLine.id"
      icon="djust-icon:remove"
      :disabled="isDeleting"
      :loading="isDeleting"
      variant="ghost"
      class="mr-2 mt-4 h-4 w-4 text-[#706F6F]"
      :ui="{ base: 'p-0 hover:bg-transparent', leadingIcon: 'h-4 w-4' }"
      @click="handleDeleteCartLine"
    />
  </div>
</template>

<script setup lang="ts">
  const props = defineProps<{
    cartLine: any;
  }>();
  const { getUserCurrency } = useUser();
  const currency = computed(() => getUserCurrency());
  const emit = defineEmits(['deleted', 'error', 'close', 'refresh']);
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);
  const cartId = computed(() => getCartId(cart.value));
  const isDeleting = ref(false);
  const localePath = useLocalePath();
  const { getFormattedPrice } = useUtils();
  const {
    getCartId,
    deleteCartLines,
    getCartOfferId,
    addOrUpdateCartLine,
    getCartProductVariantName,
    getCartProductVariantBrand,
    getCartProductVariantId,
    getCartProductVariantImageLink,
    getCartOfferPriceWithoutTax,
    getCartOfferCustomFieldValueByExternalId,
    getCartProductSku,
  } = useCart();

  const name = computed(() => getCartProductVariantName(props.cartLine));
  const adiscoRef = computed(() => getCartProductVariantId(props.cartLine));
  const supplier = computed(() => getCartProductVariantBrand(props.cartLine));
  const imageUrl = computed(() => getCartProductVariantImageLink(props.cartLine));
  const offerPriceId = computed(() => getCartOfferId(props.cartLine));
  const increment = ref(0);
  const productPageLink = computed(() =>
    localePath(`/product/${getCartProductSku(props.cartLine)}`)
  );

  const price = computed(() =>
    getCartOfferPriceWithoutTax(props.cartLine) > 0
      ? getFormattedPrice(
          parseFloat(getCartOfferPriceWithoutTax(props.cartLine)) * props.cartLine.quantity,
          currency.value
        )
      : zeroPrice
  );
  const tgap = computed(() =>
    getFormattedPrice(
      parseFloat(getCartOfferCustomFieldValueByExternalId(props.cartLine, 'TGAP')) *
        props.cartLine.quantity,
      currency.value
    )
  );
  const hasTax = computed(
    () =>
      parseFloat(getCartOfferCustomFieldValueByExternalId(props.cartLine, 'TGAP')) > 0 &&
      price.value !== zeroPrice
  );

  async function updateQuantity(newQuantity: number) {
    const lineToUpdate = [
      {
        offerPriceId: getCartOfferId(props.cartLine),
        quantity: newQuantity,
        updateAction: 'REPLACE_QUANTITY' as UpdateCartLineType,
      },
    ];
    try {
      await addOrUpdateCartLine({
        currency: currency.value,
        cartId: cartId.value,
        typeId: 'offerPriceId',
        lines: lineToUpdate,
      });
      emit('refresh');
    } catch (error) {
      console.error('Error updating cart line quantity:', error);
    }
  }
  async function handleDeleteCartLine() {
    try {
      isDeleting.value = true;
      const response = await deleteCartLines({
        cartId: cartId.value,
        lineIdsToDelete: [offerPriceId.value],
      });
      emit('deleted', response);
      increment.value++;
    } catch (error) {
      console.error('Failed to delete cart lines:', error);
      emit('error', error);
    } finally {
      isDeleting.value = false;
    }
  }
</script>
